/**
 * SyncStatusIndicator
 *
 * Zeigt den aktuellen Sync-Zustand mit dem Server.
 * Bei Fehlern: Retry-Countdown und klickbar zum manuellen Retry.
 */

import { useEffect, useState } from 'preact/hooks';
import { Loader2, Cloud, Check, AlertTriangle } from 'lucide-preact';
import {
  syncStatus,
  lastSyncError,
  nextRetryAt,
  retryCount,
  retrySync,
} from '../../stores/timesheet.store';
import styles from './SyncStatusIndicator.module.css';

export function SyncStatusIndicator() {
  const status = syncStatus.value;
  const error = lastSyncError.value;
  const retryAt = nextRetryAt.value;
  const attempts = retryCount.value;

  // Countdown für nächsten Retry
  const [countdown, setCountdown] = useState<number | null>(null);

  useEffect(() => {
    if (!retryAt) {
      setCountdown(null);
      return;
    }

    const updateCountdown = () => {
      const remaining = Math.max(0, Math.ceil((retryAt - Date.now()) / 1000));
      setCountdown(remaining);
    };

    updateCountdown();
    const intervalId = setInterval(updateCountdown, 1000);

    return () => clearInterval(intervalId);
  }, [retryAt]);

  const handleRetry = async () => {
    await retrySync();
  };

  if (status === 'idle') {
    return (
      <span className={styles.indicator} title="Nur lokal gespeichert">
        <Cloud size={14} />
        <span className={styles.label}>Lokal</span>
      </span>
    );
  }

  if (status === 'syncing') {
    return (
      <span className={`${styles.indicator} ${styles.syncing}`}>
        <Loader2 size={14} className={styles.spinner} />
        <span className={styles.label}>Sende...</span>
      </span>
    );
  }

  if (status === 'synced') {
    return (
      <span className={`${styles.indicator} ${styles.synced}`} title="Erfolgreich gesendet">
        <Check size={14} />
        <span className={styles.label}>Gesendet</span>
      </span>
    );
  }

  if (status === 'error') {
    const retryInfo =
      countdown !== null && countdown > 0
        ? `Retry #${attempts + 1} in ${countdown}s`
        : 'Klick zum Senden';

    return (
      <button
        type="button"
        className={`${styles.indicator} ${styles.error}`}
        onClick={handleRetry}
        title={`Fehler: ${error ?? 'Unbekannt'} - ${retryInfo}`}
      >
        <AlertTriangle size={14} />
        <span className={styles.label}>
          {countdown !== null && countdown > 0 ? `${countdown}s` : 'Fehler'}
        </span>
      </button>
    );
  }

  return null;
}
